// src/pages/dashboards/admin/CreateCompanyModal.jsx
// Creates a company on an annual term, and optionally its first admin login
// in the same flow (matches the "provision in one step" intent).

import React, { useState } from "react";
import { createCompany, createCompanyAdmin } from "../../../api/admin";
import { Modal, Field, Input, Select, Button } from "../../../components/ui";
import { useToast } from "../../../context/ToastContext";

const COMPANY_TIERS = ["company_starter", "company_pro"];

export default function CreateCompanyModal({ onClose, onCreated }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  // company
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("company_starter");
  const [termMonths, setTermMonths] = useState("12");

  // optional first admin
  const [addAdmin, setAddAdmin] = useState(true);
  const [aName, setAName] = useState("");
  const [aEmail, setAEmail] = useState("");
  const [aPassword, setAPassword] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const company = await createCompany({
        name: name.trim(),
        email: email.trim() ? email.trim() : undefined,
        tier,
        term_months: parseInt(termMonths, 10) || 12,
      });

      if (addAdmin && aEmail.trim() && aPassword) {
        await createCompanyAdmin(company.id, {
          name: aName.trim() || name.trim(),
          email: aEmail.trim(),
          password: aPassword,
        });
      }

      toast.success("Company created");
      onCreated?.(company);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="New company" width="max-w-xl">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Company name">
          <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Acme (Pty) Ltd" />
        </Field>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Billing email" hint="optional">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="accounts@acme.co.za" />
          </Field>
          <Field label="Tier">
            <Select value={tier} onChange={(e) => setTier(e.target.value)}>
              <option value="company_starter">Company Starter</option>
              <option value="company_pro">Company Pro</option>
            </Select>
          </Field>
        </div>

        <Field label="Term (months)">
          <Input type="number" min="1" max="60" value={termMonths} onChange={(e) => setTermMonths(e.target.value)} />
        </Field>

        {/* first admin */}
        <div className="pt-3 border-t border-tb-border dark:border-tb-d-border">
          <label className="flex items-center gap-2 text-sm font-medium mb-3">
            <input type="checkbox" checked={addAdmin} onChange={(e) => setAddAdmin(e.target.checked)} />
            Create first company admin login
          </label>

          {addAdmin && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Admin name">
                  <Input value={aName} onChange={(e) => setAName(e.target.value)} placeholder="Jane Doe" />
                </Field>
                <Field label="Admin email">
                  <Input type="email" value={aEmail} onChange={(e) => setAEmail(e.target.value)} placeholder="jane@acme.co.za" />
                </Field>
              </div>
              <Field label="Temporary password" hint="min 8 chars — share securely, they can change it after login">
                <Input type="text" value={aPassword} onChange={(e) => setAPassword(e.target.value)} placeholder="set a starter password" />
              </Field>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Creating..." : "Create company"}</Button>
        </div>
      </form>
    </Modal>
  );
}